import { useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Button, Card, Divider, Form, Input, Modal, Select, Space, Typography } from 'antd'
import { ArrowLeftOutlined, SaveOutlined } from '@ant-design/icons'
import SystemHeader from '@/widgets/layout/SystemHeader'
import { BackButton } from '@/widgets/demo/ListPrimitives'
import { StatusBadge } from '@/widgets/common'
import { useToast } from '@/widgets/common'
import { useAppNavigate } from '../../hooks/useAppNavigate'
import { applyApiFieldErrors, isHandledAuthError, municipalitiesApi, usersApi } from '@/services'
import { roleDisplayToApi } from './userMappers'
import { useAuth } from '@/hooks'

const { Title, Text } = Typography

import type { UserRole } from '@/constants'
import { USER_ROLE_KEYS, enumLabel } from '@/constants/enums'

type UserEditFormValues = {
  municipalityId: number | null
  name: string
  dept: string
  email: string
  role: UserRole
  usageStartDate: string
  usageEndDate: string
  status: string
}

interface UserEditProps {
  viewerRole?: string
}

const unlockRoles = new Set(['ADMIN', 'SYSTEM_ADMIN', '管理者', 'システム管理者'])

const allRoleOptions = USER_ROLE_KEYS.map((key) => ({
  value: key,
  label: enumLabel('roles', key),
}))

const ACTIVE_LABEL = '有効'
const INACTIVE_LABEL = '無効'

const todayInputValue = () => {
  const date = new Date()
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}

const deriveStatusLabel = (usageStartDate?: string, usageEndDate?: string) => {
  const today = todayInputValue()
  if (usageStartDate && usageStartDate > today) return INACTIVE_LABEL
  if (usageEndDate && usageEndDate < today) return INACTIVE_LABEL
  return ACTIVE_LABEL
}

const requiredLabel = (label: string) => (
  <span className="inline-flex items-center gap-1">
    <span aria-hidden className="text-sm font-bold text-red-500">
      *
    </span>
    <span>{label}</span>
  </span>
)

const UserEdit = ({ viewerRole = 'BUSINESS_CENTER' }: UserEditProps) => {
  const navigate = useAppNavigate()
  const toast = useToast()
  const { session } = useAuth()
  const [form] = Form.useForm<UserEditFormValues>()
  const { userId: userIdParam } = useParams<{ userId?: string }>()
  const userId = userIdParam ?? null
  const numericId = userId && /^\d+$/.test(userId) ? Number(userId) : NaN
  const [loading, setLoading] = useState(() => Number.isFinite(numericId))
  const [userDisplayId, setUserDisplayId] = useState('')
  const [loginId, setLoginId] = useState('')
  const [accountLocked, setAccountLocked] = useState(false)
  const [cognitoLocked, setCognitoLocked] = useState(false)
  const [initialAccountLocked, setInitialAccountLocked] = useState(false)
  const [municipalityOptions, setMunicipalityOptions] = useState<{ value: number; label: string }[]>([])
  const [currentMunicipalityId, setCurrentMunicipalityId] = useState<number | null>(null)

  const canUnlock = unlockRoles.has(viewerRole)
  const isSystemAdmin = session?.role === 'SYSTEM_ADMIN'

  const roleOptions = useMemo(() => {
    return isSystemAdmin
      ? allRoleOptions
      : allRoleOptions.filter((option) => option.value !== 'SYSTEM_ADMIN')
  }, [isSystemAdmin])

  const ownMunicipalityLabel = useMemo(() => {
    if (currentMunicipalityId == null) return ''
    const match = municipalityOptions.find((option) => option.value === currentMunicipalityId)
    return match?.label ?? ''
  }, [municipalityOptions, currentMunicipalityId])

  useEffect(() => {
    if (!Number.isFinite(numericId)) return

    let cancelled = false
    Promise.all([usersApi.getById(numericId), municipalitiesApi.list()])
      .then(([{ data }, { data: municipalities }]) => {
        if (cancelled) return
        setMunicipalityOptions(
          municipalities.map((municipality) => ({
            value: municipality.id,
            label: `${municipality.code} ${municipality.name}`,
          })),
        )
        setUserDisplayId(data.userDisplayId)
        setLoginId(data.loginId)
        setAccountLocked(data.accountLocked)
        setCognitoLocked(data.cognitoLocked)
        setInitialAccountLocked(data.accountLocked)
        setCurrentMunicipalityId(data.municipalityId ?? null)
        form.setFieldsValue({
          municipalityId: data.municipalityId ?? null,
          name: data.name,
          dept: data.department || '',
          email: data.email,
          role: roleDisplayToApi(data.role),
          usageStartDate: data.usageStartDate || todayInputValue(),
          usageEndDate: data.usageEndDate || '9999-12-31',
          status: data.status || deriveStatusLabel(data.usageStartDate, data.usageEndDate || undefined),
        })
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [form, numericId])

  const save = async () => {
    if (!Number.isFinite(numericId)) return

    let values: UserEditFormValues
    try {
      values = await form.validateFields()
    } catch (validationError: unknown) {
      if (validationError && typeof validationError === 'object' && 'errorFields' in validationError) return
      Modal.error({ content: '入力内容を確認してください。' })
      return
    }

    try {
      const pendingUnlock = initialAccountLocked && !accountLocked
      await usersApi.update(numericId, {
        municipalityId: values.municipalityId ?? undefined,
        name: values.name.trim(),
        department: values.dept?.trim() || undefined,
        email: values.email?.trim() || undefined,
        role: values.role,
        usageStartDate: values.usageStartDate,
        usageEndDate: values.usageEndDate?.trim() || null,
      })
      if (pendingUnlock) {
        try {
          await usersApi.unlock(numericId)
        } catch (unlockError) {
          if (isHandledAuthError(unlockError)) return
          Modal.error({ content: 'ユーザ情報は保存しましたが、アカウントロックの解除に失敗しました。再度お試しください。' })
          navigate('user-management')
          return
        }
      }
      toast.success('ユーザ情報を保存しました。')
      navigate('user-management')
    } catch (error) {
      if (isHandledAuthError(error)) return
      applyApiFieldErrors(form, error, {
        department: 'dept',
      })
    }
  }

  const unlock = () => {
    if (!canUnlock) return
    setAccountLocked(false)
    setCognitoLocked(false)
  }

  if (!Number.isFinite(numericId)) {
    return (
      <div className="min-h-screen bg-gray-100">
        <SystemHeader
          breadcrumbs={[
            { label: 'システム管理', page: 'submenu-admin' },
            { label: 'ユーザ管理', page: 'user-management' },
            { label: 'ユーザ情報編集' },
          ]}
        />
        <div className="mx-auto max-w-lg px-6 py-16 text-center text-gray-600">IDが不正です。</div>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100">
        <SystemHeader
          breadcrumbs={[
            { label: 'システム管理', page: 'submenu-admin' },
            { label: 'ユーザ管理', page: 'user-management' },
            { label: 'ユーザ情報編集' },
          ]}
        />
        <div className="mx-auto max-w-lg px-6 py-16 text-center text-gray-600">Loading...</div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <SystemHeader
        breadcrumbs={[
          { label: 'システム管理', page: 'submenu-admin' },
          { label: 'ユーザ管理', page: 'user-management' },
          { label: 'ユーザ情報編集' },
        ]}
      />
      <div className="mx-auto max-w-2xl px-6 py-8">
        <BackButton onClick={() => navigate('user-management')} />
        <Card>
          <Title level={4}>ユーザ情報編集</Title>
          <Divider />

          <Form<UserEditFormValues>
            form={form}
            layout="vertical"
            requiredMark={false}
            className="customer-validation-form"
            onValuesChange={(changed, allValues) => {
              if ('usageStartDate' in changed || 'usageEndDate' in changed) {
                const nextStatus = deriveStatusLabel(allValues.usageStartDate, allValues.usageEndDate)
                if (allValues.status !== nextStatus) {
                  form.setFieldValue('status', nextStatus)
                }
              }
            }}
          >
            <Form.Item label="ID">
              <Input value={userDisplayId} readOnly disabled style={{ fontFamily: 'monospace' }} />
            </Form.Item>

            <Form.Item label="ログインID">
              <Input value={loginId} readOnly disabled style={{ fontFamily: 'monospace' }} />
            </Form.Item>

            {isSystemAdmin ? (
              <Form.Item
                name="municipalityId"
                label={requiredLabel('自治体')}
                rules={[{ required: true, message: '自治体は必須です。' }]}
              >
                <Select options={municipalityOptions} placeholder="自治体を選択" />
              </Form.Item>
            ) : (
              <>
                <Form.Item name="municipalityId" hidden>
                  <Input />
                </Form.Item>
                <Form.Item label={requiredLabel('自治体')}>
                  <Input value={ownMunicipalityLabel} readOnly disabled />
                </Form.Item>
              </>
            )}

            <Form.Item
              name="name"
              label={requiredLabel('ログインユーザ名')}
              rules={[
                { required: true, message: 'ログインユーザ名は必須です。' },
                { max: 128, message: 'ログインユーザ名は128文字以内で入力してください。' },
              ]}
            >
              <Input maxLength={128} />
            </Form.Item>

            <Form.Item name="dept" label="所属" rules={[{ max: 128, message: '所属は128文字以内で入力してください。' }]}>
              <Input maxLength={128} />
            </Form.Item>

            <Form.Item
              name="email"
              label={requiredLabel('メールアドレス')}
              rules={[
                { required: true, whitespace: true, message: 'メールアドレスを入力してください。' },
                {
                  validator: (_, value) => {
                    if (!value || value.trim() === '') return Promise.resolve()
                    if (value.length > 254) return Promise.reject('メールアドレスは254文字以内で入力してください。')
                    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return Promise.reject('有効なメールアドレスを入力してください。')
                    return Promise.resolve()
                  },
                },
              ]}
            >
              <Input maxLength={254} placeholder="user@example.com" />
            </Form.Item>

            <Form.Item name="role" label="権限">
              <Select options={roleOptions} />
            </Form.Item>

            <Form.Item
              name="usageStartDate"
              label={requiredLabel('利用開始日')}
              rules={[{ required: true, message: '利用開始日は必須です。' }]}
            >
              <Input type="date" min={todayInputValue()} max="9999-12-31" />
            </Form.Item>

            <Form.Item
              name="usageEndDate"
              label={requiredLabel('利用終了日')}
              dependencies={['usageStartDate']}
              rules={[
                { required: true, message: '利用終了日は必須です。' },
                ({ getFieldValue }) => ({
                  validator: (_, value) => {
                    const start = getFieldValue('usageStartDate')
                    if (!value || !start || value >= start) return Promise.resolve()
                    return Promise.reject('利用終了日は利用開始日以降の日付を指定してください。')
                  },
                }),
              ]}
            >
              <Input type="date" min="1900-01-01" max="9999-12-31" />
            </Form.Item>

            <Form.Item name="status" label="ステータス">
              <Input readOnly disabled />
            </Form.Item>

            <Divider />
            <Title level={5}>アカウントロック</Title>
            <div className="flex flex-wrap items-center gap-3">
              <StatusBadge status={accountLocked ? 'ロック中' : 'ロックなし（利用可能）'} color={accountLocked ? 'red' : 'green'} />
              {accountLocked && canUnlock ? (
                <Button danger type="primary" onClick={unlock}>
                  アカウントロックを解除
                </Button>
              ) : null}
              {accountLocked && !canUnlock ? (
                <Text type="secondary">ロック解除は管理者以上の権限が必要です。</Text>
              ) : null}
            </div>
            {cognitoLocked && (
              <div className="mt-2">
                <Text type="secondary">※ Cognitoアカウントも無効化されています</Text>
              </div>
            )}
          </Form>
        </Card>

        <div className="mt-4 flex justify-end">
          <Space>
            <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('user-management')}>
              キャンセル
            </Button>
            <Button type="primary" icon={<SaveOutlined />} onClick={() => void save()}>
              保存
            </Button>
          </Space>
        </div>
      </div>
    </div>
  )
}

export default UserEdit
